import { get, isEmpty } from 'lodash';
import { useMutation, UseMutationOptions, UseQueryOptions, useQuery, QueryFunctionContext, QueryFunction } from 'react-query';
import {
	AddressSearchRequest,
	CreateMailroomInvoiceRequestDto,
	CreateMailroomInvoiceResponseDto,
	CreateMailroomWorkflowRequestDto,
	CreateMailroomWorkflowResponseDto,
	MetadataDto,
	PagedResultMetadataDto,
	PagedResultPropertyAddressSearchResultDto,
	PagedUserProfileDto,
	SupplierSearchDto,
	UserProfile,
	UserServiceContractsDtosUserProfileDto
} from './types';
import { useBaseQuery } from './util/basequery';
import { useHttpClient } from './util/httpclient';

export interface Pagination {
	currentPage: number;
	pageSize: number;
}

export const useGetMail = <TResponse = PagedResultMetadataDto>(
	pagination?: Pagination,
	options?: UseQueryOptions<unknown, unknown, TResponse>
) => {
	const queryString = `pageSize=${get(pagination, 'pageSize', get(pagination, 'currentPage', 1))}&currentPage=${get(
		pagination,
		'currentPage',
		1
	)}`;

	const { isAuthenticated, json, abort } = useHttpClient({
		path: `/mailroom/mail?${queryString}`,
		method: 'GET',
		baseUrl: process.env.REACT_APP_Mailroom_Backend_Url
	});

	const queryKey = ['useGetMail', queryString];
	const queryFn = () => async () => await json<TResponse>();
	const _options = { ...options };

	_options.enabled = isAuthenticated && options?.enabled;
	return useBaseQuery<TResponse>({
		queryKey,
		queryFn,
		options: _options,
		abortFn: () => abort()
	});
};

interface UseGetMailByIdHookProps {
	entityId?: string;
}

export const useGetMailById = ({ entityId }: UseGetMailByIdHookProps, options?: UseQueryOptions<unknown, unknown, MetadataDto>) => {
	const { json, isAuthenticated, abort } = useHttpClient({
		path: `/mailroom/mail/${entityId || 'next'}`,
		method: 'GET',
		baseUrl: process.env.REACT_APP_Mailroom_Backend_Url
	});
	const queryKey = ['useGetMailById', entityId];
	const queryFn = () => async () => await json();
	const _options = { ...options };
	_options.enabled = isAuthenticated && options?.enabled;
	return useBaseQuery<MetadataDto>({ queryKey, queryFn, options: _options, abortFn: () => abort() });
};

interface UseGetDocumentByIdHookProps {
	id: number;
}

export type BlobObjectURL = string;

export const useGetDocumentById = (
	{ id }: UseGetDocumentByIdHookProps,
	options?: UseQueryOptions<unknown, unknown, { documentUri: string }>
) => {
	const { blob, isAuthenticated, abort, json } = useHttpClient({
		path: `/mailroom/document/${id}`,
		method: 'GET',
		baseUrl: process.env.REACT_APP_Mailroom_Backend_Url,
		ignoreDefaultHeaders: false
	});
	const queryKey = ['useGetDocumentById', id];
	// const queryFn = () => async () => await blob();
	const queryFn = (): QueryFunction<any> => (c) => json();

	return useBaseQuery<{ documentUri: string }>({
		queryKey,
		queryFn,
		abortFn: abort,
		options: { ...options, enabled: isAuthenticated && options?.enabled }
	});
};

interface UseGetPropertyIdProps {
	propertySearch: AddressSearchRequest;
}

export const useGetPropertyId = <TResult = PagedResultPropertyAddressSearchResultDto, TRequest = AddressSearchRequest>(
	{ propertySearch }: UseGetPropertyIdProps,
	options?: UseQueryOptions<TRequest, unknown, TResult>
) => {
	const { json, isAuthenticated, abort } = useHttpClient({
		path: `/mailroom/property/search`,
		method: 'POST',
		baseUrl: process.env.REACT_APP_Mailroom_Backend_Url
	});

	const queryKey = ['useGetPropertyId', propertySearch];

	const queryFn = (): QueryFunction<TRequest> => (c) => json(c.queryKey[1]);

	return useBaseQuery<TResult, TRequest>({
		queryKey,
		queryFn,
		options: { ...options, enabled: isAuthenticated && options?.enabled },
		abortFn: abort
	});
};

///api/mailroom/workflow/create
export const useCreateWorkflow = <TVariables = CreateMailroomWorkflowRequestDto, TResponse = CreateMailroomWorkflowResponseDto>(
	options?: UseMutationOptions<TResponse, TResponse, TVariables>
) => {
	const client = useHttpClient({ path: `/mailroom/workflow/create`, method: 'POST' });
	return useMutation(['useCreateWorkflow'], async (d: TVariables) => await client.json<TResponse>(d), { ...options });
};

///api/mailroom/invoice/create
export const useCreateInvoice = <TVariables = CreateMailroomInvoiceRequestDto, TResponse = CreateMailroomInvoiceResponseDto>(
	options?: UseMutationOptions<TResponse, TResponse, TVariables>
) => {
	const client = useHttpClient({ path: `/mailroom/invoice/create`, method: 'POST' });

	return useMutation(['useCreateInvoice'], async (d: TVariables) => await client.json<TResponse>(d), { ...options });
};

export const useUserProfileLoginQuery = <TResponse = UserProfile[]>(
	headers: HeadersInit,
	options?: UseQueryOptions<undefined, Error, TResponse>
) => {
	const { json } = useHttpClient({
		path: `/mailroom/user/profiles`,
		method: 'GET',
		headers
	});
	const queryKey = ['useUserProfileLoginQuery'];
	const queryFn = (): QueryFunction<undefined> => () => json();
	return useQuery(queryKey, queryFn(), { refetchOnWindowFocus: false, ...options });
};
interface UseSupplierSearchDtoProps {
	search?: SupplierSearchDto;
	currentPage?: number;
	pageSize?: number;
}
export const useGetSupplierQuery = <TResult = PagedUserProfileDto, TRequest = SupplierSearchDto>(
	{ search, currentPage = 1, pageSize = 5 }: UseSupplierSearchDtoProps,
	options?: UseQueryOptions<TRequest, unknown, TResult>
) => {
	const { json, isAuthenticated, abort } = useHttpClient({
		path: `/mailroom/supplier/search?pageSize=${pageSize}&currentPage=${currentPage}`,
		method: 'POST',
		baseUrl: process.env.REACT_APP_Mailroom_Backend_Url
	});

	const queryKey = ['useGetSupplierQuery', search];

	const queryFn = (): QueryFunction<TRequest> => (c) => json(c.queryKey[1]);

	return useBaseQuery<TResult, TRequest>({
		queryKey,
		queryFn,
		options: { ...options, enabled: isAuthenticated && options?.enabled },
		abortFn: abort
	});
};
